import { EnvironmentGroupId } from '@/react/portainer/environments/types';

import { Directive, ChangeWindow } from '../../../types';

/**
 * Access mode option for storage classes
 */
export interface AccessModeOption {
  Name: string;
  Description: string;
}

/**
 * Available access mode options for storage classes
 */
export const availableAccessModes: AccessModeOption[] = [
  {
    Name: 'RWO',
    Description: 'Allow read-write from a single pod only (RWO)',
  },
  {
    Name: 'RWX',
    Description:
      'Allow read-write access from one or more pods concurrently (RWX)',
  },
];

/**
 * Storage class configuration for Kubernetes
 */
export interface StorageClass {
  name: string;
  accessModes: string[];
  allowVolumeExpansion: boolean;
}

/**
 * Ingress controller configuration for Kubernetes
 */
export interface IngressController {
  name: string;
}

/**
 * Complete form values for Kubernetes Setup Policy form.
 * Combines common policy fields (name, environmentGroupIds) with type-specific fields.
 * This is the type used by Formik in KubernetesSetupPolicyForm.
 */
export interface KubernetesSetupFormValues {
  /** Policy name */
  name: string;
  /** Environment groups to apply the policy to */
  environmentGroupIds: Array<EnvironmentGroupId>;
  // Networking - Services
  /** Use LoadBalancer service type */
  useLoadBalancer: boolean;
  // Networking - Ingresses
  /** List of ingress controllers */
  ingressControllers: IngressController[];
  /** Allow using no ingress class */
  allowNoneIngressClass: boolean;
  /** Ingress availability per namespace */
  ingressAvailabilityPerNamespace: boolean;
  /** Restrict standard user ingress writes */
  restrictStandardUserIngressW: boolean;
  // Change Window
  /** GitOps change window configuration */
  changeWindow: ChangeWindow;
  // Resources and Metrics
  /** Enable resource over-commit */
  enableResourceOverCommit: boolean;
  /** Resource over-commit percentage */
  resourceOverCommitPercentage: number;
  /** Use server metrics */
  useServerMetrics: boolean;
  // Storage
  /** List of storage classes */
  storageClasses: StorageClass[];
}

/**
 * Type-specific form data for Kubernetes Setup Policy (without common fields).
 * Used by inner form components.
 */
export interface KubernetesSetupFormData {
  // Networking - Services
  /** Use LoadBalancer service type */
  useLoadBalancer: boolean;

  // Networking - Ingresses
  /** List of ingress controllers */
  ingressControllers: IngressController[];
  /** Allow using no ingress class */
  allowNoneIngressClass: boolean;
  /** Ingress availability per namespace */
  ingressAvailabilityPerNamespace: boolean;
  /** Restrict standard user ingress writes */
  restrictStandardUserIngressW: boolean;

  // Change Window
  /** GitOps change window configuration */
  changeWindow: ChangeWindow;

  // Resources and Metrics
  /** Enable resource over-commit */
  enableResourceOverCommit: boolean;
  /** Resource over-commit percentage */
  resourceOverCommitPercentage: number;
  /** Use server metrics */
  useServerMetrics: boolean;

  // Storage
  /** List of storage classes */
  storageClasses: StorageClass[];
}

/**
 * Ingress class configuration for the backend API (PascalCase for JSON serialization)
 */
export interface IngressClassPayload {
  Name: string;
  Type: string;
  GloballyBlocked: boolean;
  BlockedNamespaces: string[];
}

/**
 * Storage class configuration for the backend API (PascalCase for JSON serialization)
 */
export interface StorageClassPayload {
  Name: string;
  AccessModes: string[];
  AllowVolumeExpansion: boolean;
}

/**
 * Stored/API payload for Kubernetes Setup Policy
 * This matches the actual backend API structure
 */
export interface KubernetesSetupPolicyPayload {
  UseLoadBalancer: Directive<boolean>;
  UseServerMetrics: Directive<boolean>;
  EnableResourceOverCommit: Directive<boolean>;
  ResourceOverCommitPercentage: Directive<number>;
  IngressAvailabilityPerNamespace: Directive<boolean>;
  AllowNoneIngressClass: Directive<boolean>;
  StorageClasses: Array<Directive<StorageClassPayload>>;
  IngressClasses: Array<Directive<IngressClassPayload>>;
  RestrictStandardUserIngressW: Directive<boolean>;
  DeploymentOptions: Directive<Record<string, unknown>>;
  EndpointChangeWindow: Directive<ChangeWindow>;
}

/**
 * Default initial values for Kubernetes Setup Policy type-specific fields (create mode)
 */
export const KUBERNETES_SETUP_INITIAL_VALUES: KubernetesSetupFormData = {
  useLoadBalancer: false,
  ingressControllers: [],
  allowNoneIngressClass: false,
  ingressAvailabilityPerNamespace: false,
  restrictStandardUserIngressW: false,
  changeWindow: {
    Enabled: false,
    StartTime: '22:00',
    EndTime: '02:00',
  },
  enableResourceOverCommit: false,
  resourceOverCommitPercentage: 0,
  useServerMetrics: false,
  storageClasses: [],
};

/**
 * Default access mode for new storage classes
 */
export const DEFAULT_ACCESS_MODE = 'RWO';

/**
 * Default initial values for complete Kubernetes Setup Policy form (create mode)
 */
export const KUBERNETES_SETUP_FORM_INITIAL_VALUES: KubernetesSetupFormValues = {
  name: '',
  environmentGroupIds: [],
  ...KUBERNETES_SETUP_INITIAL_VALUES,
};
